import React from 'react'
import { ArrowRight, Code2, Database, Cloud, Terminal, Cpu, Layers, Box, GitBranch, Server, Zap } from 'lucide-react'
import Button from './Button'
import SectionBackground from './SectionBackground'

const Hero = () => {
  // Developer tech stack icons data
  const techIcons = [
    { Icon: Code2, label: 'React', x: 10, y: 15, color: 'text-blue-400' },
    { Icon: Server, label: 'Node.js', x: 85, y: 20, color: 'text-green-400' },
    { Icon: Terminal, label: 'TypeScript', x: 20, y: 70, color: 'text-blue-500' },
    { Icon: Layers, label: 'CSS', x: 75, y: 65, color: 'text-purple-400' },
    { Icon: Cpu, label: 'Python', x: 15, y: 40, color: 'text-yellow-400' },
    { Icon: Cloud, label: 'AWS', x: 90, y: 45, color: 'text-orange-400' },
    { Icon: Database, label: 'MongoDB', x: 12, y: 85, color: 'text-green-500' },
    { Icon: Zap, label: 'Next.js', x: 88, y: 80, color: 'text-gray-400' },
    { Icon: GitBranch, label: 'Git', x: 50, y: 10, color: 'text-red-400' },
    { Icon: Box, label: 'Docker', x: 45, y: 90, color: 'text-cyan-400' },
  ]

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24 overflow-hidden"
    >
      <SectionBackground variant="hero" />
      
      {/* Animated Background Tech Icons */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {techIcons.map((tech, index) => {
          const Icon = tech.Icon
          
          return (
            <div
              key={tech.label}
              className={`absolute opacity-10 dark:opacity-15 animate-float ${tech.color}`}
              style={{
                left: `${tech.x}%`,
                top: `${tech.y}%`,
                animationDelay: `${index * 0.5}s`,
              }}
            >
              <Icon size={35} strokeWidth={1.5} />
            </div>
          )
        })}
      </div>

      <div className="max-w-7xl mx-auto w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center animate-fade-in">
          
          {/* Left Side - Content */}
          <div className="text-left space-y-8">
            {/* Main Headline */}
            <div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 dark:text-slate-100 mb-4 leading-tight">
                Building Tomorrow's{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
                  Software Solutions
                </span>
              </h1>
              
              {/* Tagline */}
              <p className="text-lg text-slate-600 dark:text-slate-300 max-w-xl">
                Software Engineering Student at Java Institute for Advanced Technology,
                specializing in modern web technologies and scalable application development.
                Transforming ideas into robust digital solutions.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button href="#contact" variant="primary" size="lg">
                Get In Touch
              </Button>
              <Button href="#projects" variant="secondary" size="lg">
                View My Work
              </Button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 pt-8">
              <div>
                <div className="text-4xl font-bold text-slate-900 dark:text-slate-100">1st</div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">year<br />student</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-slate-900 dark:text-slate-100">10+</div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">projects<br />completed</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-slate-900 dark:text-slate-100">100%</div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">dedication<br />& passion</div>
              </div>
            </div>
          </div>

          {/* Right Side - Image with Floating Tags */}
          <div className="relative h-[700px] flex items-center justify-center">
            
            {/* Main Image - Larger Size */}
            <div className="relative z-10">
              <img 
                src="/hero.png" 
                alt="Professional" 
                className="w-full h-[650px] object-cover rounded-3xl"
              />
            </div>

            {/* Floating Tags - Positioned at marked spots with higher z-index */}
            {/* Middle Left - Full-Stack Development (at waist level) */}
            <div className="absolute top-[55%] left-8 bg-white dark:bg-slate-800 px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2 hover:scale-110 transition-transform duration-300 z-20">
              <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-cyan-400 rounded-full flex items-center justify-center">
                <Code2 className="w-3 h-3 text-white" />
              </div>
              <span className="font-semibold text-xs text-slate-900 dark:text-slate-100">Full-Stack Dev</span>
            </div>

            {/* Middle Right - Cloud Architecture */}
            <div className="absolute top-[40%] right-0 bg-white dark:bg-slate-800 px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2 hover:scale-110 transition-transform duration-300 z-20">
              <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                <Cloud className="w-3 h-3 text-white" />
              </div>
              <span className="font-semibold text-xs text-slate-900 dark:text-slate-100">Cloud Solutions</span>
            </div>

            {/* Bottom Right - Database Design */}
            <div className="absolute bottom-12 right-20 bg-white dark:bg-slate-800 px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2 hover:scale-110 transition-transform duration-300 z-20">
              <div className="w-6 h-6 bg-gradient-to-br from-pink-400 to-red-400 rounded-full flex items-center justify-center">
                <Database className="w-3 h-3 text-white" />
              </div>
              <span className="font-semibold text-xs text-slate-900 dark:text-slate-100">Database Design</span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-slate-400 dark:border-slate-600 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-slate-600 dark:bg-slate-400 rounded-full mt-2"></div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero